import { useEffect } from 'react'

import type { Location } from 'history'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useLocation, useParams } from 'react-router-dom'

import { useDecodedAccessToken } from '@/app/AuthProvider'
import { replace } from '@helpers/windowLocation'

/**
 * Flag-gated soft redirect from an accounts-portal settings page to its
 * home-portal equivalent. Flag off renders the page as-is; flag on forwards via
 * a full-page `replace` (destination is outside the `/acct` basename, so
 * react-router `<Redirect>` can't reach it). `hpTemplate` `:param`s are filled
 * from the route params, resolving the `my` alias and missing ids from the
 * token; query strings are preserved; unresolvable ids fall back to sign-in.
 */
const HpSettingsRedirect = ({
  hpTemplate,
  children,
}: {
  hpTemplate: string
  children: React.ReactNode
}) => {
  const { 'my-account-hp-nav': enabled = false } = useFlags<{
    'my-account-hp-nav'?: boolean
  }>()
  const params = useParams<Record<string, string | undefined>>()
  // react-router-dom's v5 types don't resolve under tsgolint, so this hook comes
  // back as an error type. Assert the `history` package's own type.
  const location = useLocation() as Location
  const { accountId: tokenAccountId, personId: tokenPersonId } =
    useDecodedAccessToken() ?? {}

  useEffect(() => {
    if (!enabled) return

    // Unresolved names are collected rather than tracked with a boolean flag:
    // the callback below runs outside this scope as far as control-flow analysis
    // is concerned, so a `let` flag reads as never-reassigned.
    const missingIds: string[] = []
    const resolved = hpTemplate.replace(/:(\w+)/g, (_match, name: string) => {
      let value = params[name]
      // `my` is a placeholder for "the current user"; resolve it (and any
      // absent id) from the token, same as MyAccountRedirect.
      if (value === 'my' || value == null) {
        if (name === 'accountId') value = tokenAccountId ?? undefined
        else if (name === 'personId') value = tokenPersonId ?? undefined
      }
      if (!value) missingIds.push(name)
      return encodeURIComponent(value ?? '')
    })

    if (missingIds.length) {
      const returnTo = encodeURIComponent(location.pathname + location.search)
      replace(`/acct/signin?redirectAfterLogin=${returnTo}`)
      return
    }

    replace(resolved + location.search)
  }, [
    enabled,
    hpTemplate,
    ...Object.values(params),
    location.pathname,
    location.search,
    tokenAccountId,
    tokenPersonId,
  ])

  if (enabled) return null
  return <>{children}</>
}

export default HpSettingsRedirect
